import { Injectable, inject } from '@angular/core';

import { SharedFeaturesApiService, UserService } from '@frontend/vanilla/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';

import { LoyaltyLoginValidation, ValidateLoyaltyLoginRequest } from './loyalty-login.models';

/**
 * @whatItDoes
 * This service is responsible for validating the loyalty login and providing the validation result.
 *
 * @description
 * Service for handling loyalty login validation.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class LoyaltyLoginService {
    private sharedFeaturesApiService = inject(SharedFeaturesApiService);
    private userService = inject(UserService);

    /**
     * The current loyalty login validation object.
     */
    loyaltyValidation: LoyaltyLoginValidation;

    private loyaltyLoginValidationEvents = new BehaviorSubject<LoyaltyLoginValidation | null>(null);
    private loaded: boolean;

    /**
     * Allows to subscribe to loyalty login validation values.
     */
    get loyaltyLoginValidation(): Observable<LoyaltyLoginValidation | null> {
        return this.loyaltyLoginValidationEvents;
    }

    /**
     * Validates the loyalty login.
     *
     * @param request - The request object containing the loyalty login data.
     * @returns A promise that resolves to the loyalty login validation object or null if already loaded.
     */
    async validate(request: ValidateLoyaltyLoginRequest): Promise<LoyaltyLoginValidation | null> {
        if (!this.loaded && this.userService.isAuthenticated) {
            this.loaded = true;

            const response = await firstValueFrom(this.sharedFeaturesApiService.post('loyaltylogin/validate', request));
            this.loyaltyLoginValidationEvents.next(response);
            this.loyaltyValidation = response;

            return response;
        }

        return null;
    }
}
